import { accounts, CredentialResponse } from 'google-one-tap';
import { User } from '../../../../../../../graphql.generated';
import { useSelector } from 'react-redux';
import { parseCookies } from 'nookies';
import { useRouter } from 'next/router';
import { FetchUserDocument, FetchUserQuery } from '@zoopla/user-queries';
import { useEffect, useRef, useState } from 'react';
import {
    trackGoogleOneTapPromptError,
    trackGoogleOneTapSuccess
} from '../../../../GoogleOneTap/index.tracking';
import { triggerSuccessfulAuthentication } from '../../../../../utils/triggerSuccessfulAuthentication';
import { CONSENT_COOKIE } from '../../../../../constants/tracking';
import { consentNotShownInLastTenMins } from '@zoopla/consent-utils';
import { getUserSelector } from '@zoopla/redux-reducer-user';
import {
    SocialSignInGoogleOneTapDocument,
    SocialSignInGoogleOneTapMutation,
    SocialSignInGoogleOneTapMutationVariables
} from '../../../queries/googleOneTap.generated';
import { SignInAction } from '../useSocialSignIn.types';
import { isNewUser } from '../../../../../utils';
import { trackAuthSegmentEvent } from '../../../../../utils/segmentTrack';
import { getSegmentData } from '../../../../../utils/getSegmentData';
import { formatNewRelicErrorData } from '../../../../../utils/formatNewRelicErrorData';
import { formatNewRelicSuccessData } from '../../../../../utils/formatNewRelicSuccessData';
import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';

export const useGoogleOneTap = (
    { onSuccess }: SignInAction,
    currentUser: User = {}
) => {
    const user = useSelector(getUserSelector);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const router = useRouter();
    const oneTapCallbackRef = useRef<(vars: { accessToken: string }) => void>();

    const init = () => {
        if (!window.google) {
            return;
        }
    };

    const { lazyFetch: triggerSocialUserTracking } =
        useLazyFetchGraphql<FetchUserQuery>(FetchUserDocument, {
            onCompleted({ user }) {
                if (isNewUser(currentUser, user?.creationDate)) {
                    trackGoogleOneTapSuccess('registration');
                    trackAuthSegmentEvent(
                        'reg',
                        getSegmentData(router.pathname)
                    );
                } else {
                    trackGoogleOneTapSuccess('signin');
                    trackAuthSegmentEvent(
                        'signin',
                        getSegmentData(router.pathname)
                    );
                }

                onSuccess(user || undefined);
            },
            onError() {
                onSuccess();
            }
        });

    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SocialSignInGoogleOneTapMutation,
        SocialSignInGoogleOneTapMutationVariables
    >(SocialSignInGoogleOneTapDocument, {
        onCompleted({ socialSignInGoogleOneTap }) {
            if (socialSignInGoogleOneTap?.success === 0) {
                setIsAuthenticating(false);
                trackGoogleOneTapPromptError(router.asPath);
                return;
            }
            triggerSuccessfulAuthentication();
            triggerSocialUserTracking();
        },
        onError() {
            setIsAuthenticating(false);
            trackGoogleOneTapPromptError(router.asPath);
        }
    });

    useEffect(() => {
        oneTapCallbackRef.current = signInMutation;
    });

    const signInToZooplaWithGoogle = (
        credentialResponse: CredentialResponse
    ) => {
        if (credentialResponse) {
            oneTapCallbackRef.current?.({
                accessToken: credentialResponse.credential
            });

            window.newrelic?.addPageAction(
                ...formatNewRelicSuccessData({
                    name: 'google-onetap-auth'
                })
            );
        } else {
            window.newrelic?.addPageAction(
                ...formatNewRelicErrorData({
                    name: 'google-onetap-auth',
                    type: 'missing-credential'
                })
            );
        }
    };

    const onLogin = async () => {
        const isGuestUser = user.loggedIn === false && user.loading === false;
        const consentCookie = parseCookies()[CONSENT_COOKIE];

        if (!window.google) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const googleAccounts: accounts = (window.google as unknown as any)
            ?.accounts;

        if (consentNotShownInLastTenMins(consentCookie) && isGuestUser) {
            // Note: TS get namespace `google` from the Google Maps package caused type error
            googleAccounts?.id.initialize({
                client_id:
                    '696203622959-7kq1qaikjuk02838oc5ckv0t3a4329tq.apps.googleusercontent.com',
                context: 'signin',
                callback: signInToZooplaWithGoogle
            });

            googleAccounts?.id.prompt((notification) => {
                const isNotDisplayed = notification.isNotDisplayed();

                if (isNotDisplayed || notification.isSkippedMoment()) {
                    console.log(notification.getNotDisplayedReason());
                }

                if (isNotDisplayed) {
                    const reason = notification.getNotDisplayedReason();

                    if (
                        !reason ||
                        reason === 'opt_out_or_no_session' ||
                        reason === 'suppressed_by_user'
                    ) {
                        return;
                    }

                    window.newrelic?.addPageAction(
                        ...formatNewRelicErrorData({
                            name: 'google-onetap-auth',
                            type: reason
                        })
                    );
                }
            });
        }
    };

    return {
        init,
        onLogin,
        isAuthenticating
    };
};
